import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Flex, Typography } from "antd";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { TitlePageFillersDropdown, journalsQueries } from "@/entities/journal";
import {
  JournalSectionPageHeader,
  JournalSectionPagination,
  JournalSectionRecordsToolbar,
  useJournalSectionPagination,
} from "@/entities/journal/ui";
import { JournalSectionNavTitle, useJournalSectionPersonnelPageConfig } from "@/features/journal-section-personnel";
import {
  Can,
  EntityBaseAbilityBuilder,
  useUpdateAbility,
} from "@/shared/lib/casl";
import { useCoordinator } from "@shared/lib/hooks";
import { useAppRoutes } from "@shared/lib/react-router";
import { useDebounce, useIsUnifiedMode } from "@shared/utils";
import { JournalBulkSendToSignModal } from "../../journal-bulk-send-to-sign";
import { useSectionTwoController } from "../model/use-section-second-controller";
import {
  JournalSectionSecondActiveFilterChips,
  JournalSectionSecondFilter,
} from "./journal-section-second-filter/journal-section-second-filter";
import {
  getSectionSecondTableColumns,
  SectionSecondTable,
} from "./section-table/section-second-table";
import "./journals-section-second.scss";

export function JournalsSectionSecond() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const appRoutes = useAppRoutes();
  const { titleId, mainJournalId, projectId, objectId } = useCoordinator();
  const { recordsTitle, recordsPath } = useJournalSectionPersonnelPageConfig(2);

  const isFormOrViewPage = location.pathname.includes("/form/") || location.pathname.includes("/view/");
  const isPersonnelPage = location.pathname.includes("/personnel");

  const statusFromUrl = searchParams.get("status");
  const selectedStatuses = useMemo(
    () => (statusFromUrl ? statusFromUrl.split(",").filter(Boolean) : []),
    [statusFromUrl],
  );

  const [search, setSearch] = useState("");
  const [signerIds, setSignerIds] = useState<string[]>([]);
  const [specialJournalDate, setSpecialJournalDate] = useState("");
  const [dateHandover, setDateHandover] = useState("");

  const pagination = useJournalSectionPagination(10);
  const { page, setPage, limit } = pagination;

  const setSelectedStatuses = useCallback<Dispatch<SetStateAction<string[]>>>(
    (updater) => {
      setSearchParams(
        (prev) => {
          const current = prev.get("status")?.split(",").filter(Boolean) ?? [];
          const next = typeof updater === "function" ? updater(current) : updater;
          const params = new URLSearchParams(prev);
          if (next.length) {
            params.set("status", next.join(","));
          } else {
            params.delete("status");
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    setPage(1);
  }, [statusFromUrl, setPage]);

  const delayedSearch = useDebounce(search, 1000);

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const { data: dataTitle, isLoading: isTitleLoading } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const { data: mainJournal } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const {
    data: records,
    isFetching,
    refetch,
    isLoading,
  } = useQuery({
    ...journalsQueries.getSection2({
      sectionTwoId: mainJournal?.mainJournal?.section2?.sectionId || "",
      search: delayedSearch,
      page,
      limit,
      status: selectedStatuses.join(","),
      specialJournalDate,
      dateHandover,
      signerId: signerIds.join(","),
    }),
    enabled: !!mainJournal?.mainJournal?.section2?.sectionId,
  });

  const displayedRecords = useMemo(() => {
    if (!records) return records;

    const bannerStatus = selectedStatuses[0];
    const shouldSortByCreateDate =
      selectedStatuses.length === 1 &&
      (bannerStatus === "AWAITING_SIGNATURE" || bannerStatus === "REJECTED");

    if (!shouldSortByCreateDate || !records.section2List?.length) {
      return records;
    }

    return {
      ...records,
      section2List: [...records.section2List].sort((left, right) => {
        const leftTime = left.createDate ? Date.parse(left.createDate) : 0;
        const rightTime = right.createDate ? Date.parse(right.createDate) : 0;
        return leftTime - rightTime;
      }),
    };
  }, [records, selectedStatuses]);

  const sectionTwoId = mainJournal?.mainJournal?.section2?.sectionId;
  const sectionTwoListId = mainJournal?.mainJournal?.section2?.sectionListId;

  useEffect(() => {
    const id = searchParams.get("itemNoVersionId");
    const act = searchParams.get("action");

    if (id && act === "EDIT") {
      navigate(
        appRoutes.journals.section2Form(projectId, objectId, mainJournalId, titleId, id),
      );
      const params = new URLSearchParams(searchParams);
      params.delete("action");
      params.delete("itemNoVersionId");
      setSearchParams(params);
    } else if (id && act === "VIEW") {
      navigate(
        appRoutes.journals.section2View(projectId, objectId, mainJournalId, titleId, id),
      );
      const params = new URLSearchParams(searchParams);
      params.delete("action");
      params.delete("itemNoVersionId");
      setSearchParams(params);
    } else if (!id && act === "CREATE") {
      navigate(
        appRoutes.journals.section2Form(projectId, objectId, mainJournalId, titleId, "new"),
      );
      const params = new URLSearchParams(searchParams);
      params.delete("action");
      setSearchParams(params);
    }
  }, [searchParams, navigate, projectId, objectId, mainJournalId, titleId, setSearchParams]);

  const signersList = useMemo(() => {
    const rawRepresentatives = [
      ...(dataTitle?.mainJournal?.titleJournal?.developer?.representatives ?? []),
      ...(dataTitle?.mainJournal?.titleJournal?.technicalCustomer?.representatives ?? []),
    ];

    return Object.values(
      rawRepresentatives.reduce<Record<string, (typeof rawRepresentatives)[0]>>((acc, rep) => {
        const id = rep.user?.userId;
        if (id && !acc[id]) {
          acc[id] = rep;
        }
        return acc;
      }, {}),
    );
  }, [dataTitle]);

  const isDisabled = signersList.length === 0;

  const handleOpenOverlay = (recordId?: string) => {
    if (isDisabled) {
      return;
    }

    if (recordId) {
      navigate(
        appRoutes.journals.section2Form(projectId, objectId, mainJournalId, titleId, recordId),
      );
    } else {
      navigate(
        appRoutes.journals.section2Form(projectId, objectId, mainJournalId, titleId, "new"),
      );
    }
  };

  const [isBulkSendModalOpen, setIsBulkSendModalOpen] = useState(false);

  const { handleSendToSignBatch } = useSectionTwoController(
    sectionTwoListId!,
    sectionTwoId!,
    {
      onSendToSignBatchSuccess: () => {
        setIsBulkSendModalOpen(false);
        refetch();
      },
    },
  );

  const handleStartBulkSign = () => {
    setIsBulkSendModalOpen(true);
  };

  const isUnifiedMode = useIsUnifiedMode();
  const resolveBulkSendTable = useCallback(
    (data: unknown) => {
      const noop = () => {};
      return {
        rowKey: "itemNoVersionId",
        tableClassName: "journals-section-second-table",
        dataSource:
          (data as { section2List?: Record<string, unknown>[] })?.section2List ??
          [],
        columns:
          getSectionSecondTableColumns(
            {
              onDelete: noop,
              onGetVersions: noop,
              onSign: noop,
              onReject: noop,
              onRollback: noop,
              onEdit: noop,
              onView: noop,
            },
            {},
            sectionTwoListId || "",
            sectionTwoId || "",
            isUnifiedMode,
            true,
          ) ?? [],
      };
    },
    [sectionTwoListId, sectionTwoId, isUnifiedMode],
  );

  useUpdateAbility({
    // @ts-expect-error я тебя ненавижу
    permissions: records?.permissions,
    abilityBuilder: EntityBaseAbilityBuilder(),
  });
  return (
    <>
      {isFormOrViewPage || isPersonnelPage ? (
        <Outlet />
      ) : (
        <>
      <Flex className="journals-section-second" vertical>
        <div className="table-container">
          {!isTitleLoading ? (
            <JournalSectionPageHeader
              banner={
                isDisabled ? (
                  <>
                    Для добавления записи в Раздел 2 не хватает информации
                    в Титульном листе в блоке{" "}
                    <span className="journal-section-page-header__hint-link">
                      Уполномоченный представитель застройщика или
                      технического заказчика, лица, ответственного за
                      эксплуатацию здания, сооружения, или регионального
                      оператора
                    </span>
                    .{" "}
                    <TitlePageFillersDropdown
                      sectionType="sectionTwo"
                      sectionId={sectionTwoId}
                    />
                  </>
                ) : undefined
              }
              title={
                <JournalSectionNavTitle
                  recordsTitle={recordsTitle}
                  recordsPath={recordsPath}
                  activeTab="records"
                />
              }
              actions={
                <Can I="edit" a="section_2">
                  <JournalSectionRecordsToolbar
                    onStartBulkSign={handleStartBulkSign}
                    showBulkActions={!isDisabled}
                    addButton={
                      <Button
                        className="journals-section-second-add"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => handleOpenOverlay()}
                        disabled={isDisabled || !(sectionTwoId || sectionTwoListId)}
                        style={{
                          backgroundColor: isDisabled
                            ? "rgba(119, 119, 119, 1)"
                            : undefined,
                          borderColor: isDisabled
                            ? "rgba(119, 119, 119, 1)"
                            : undefined,
                        }}
                      >
                        Добавить запись
                      </Button>
                    }
                  />
                </Can>
              }
            />
          ) : (
            <Flex className="journals-section-second-hat" justify="space-between">
              <JournalSectionNavTitle
                recordsTitle={recordsTitle}
                recordsPath={recordsPath}
                activeTab="records"
              />
            </Flex>
          )}
          <Typography.Paragraph
            className="journals-section-second-paragraph"
            type="secondary"
          >
            Перечень специальных журналов, в которых ведется учет выполнения
            работ, а также журналов авторского надзора лица, осуществляющего
            подготовку проектной документации
          </Typography.Paragraph>
          <Flex
            className="journals-section-second-pageable"
            justify="space-between"
            align="center"
          >
            <JournalSectionSecondFilter
              onSearchChange={handleSearchChange}
              selectedStatuses={selectedStatuses}
              setSelectedStatuses={setSelectedStatuses}
              signerIds={signerIds}
              setSignerIds={setSignerIds}
              specialJournalDate={specialJournalDate}
              setSpecialJournalDate={setSpecialJournalDate}
              dateHandover={dateHandover}
              setDateHandover={setDateHandover}
              signersList={signersList}
              onFiltersApply={() => setPage(1)}
            />
          </Flex>
          <JournalSectionSecondActiveFilterChips
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            signerIds={signerIds}
            setSignerIds={setSignerIds}
            specialJournalDate={specialJournalDate}
            setSpecialJournalDate={setSpecialJournalDate}
            dateHandover={dateHandover}
            setDateHandover={setDateHandover}
            signersList={signersList}
            onFilterRemove={() => setPage(1)}
          />
          <SectionSecondTable
            sectionTwoId={sectionTwoId}
            sectionTwoListId={sectionTwoListId}
            isSelecting={false}
            onRowSelect={() => {}}
            onRecordEdit={handleOpenOverlay}
            onRecordView={(recordId) =>
              navigate(
                appRoutes.journals.section2View(projectId, objectId, mainJournalId, titleId, recordId),
              )
            }
            selectedRows={[]}
            records={displayedRecords}
            isLoading={isLoading || isFetching}
            refetch={refetch}
          />
          <JournalBulkSendToSignModal
            open={isBulkSendModalOpen}
            onClose={() => setIsBulkSendModalOpen(false)}
            section={2}
            sectionTwoId={sectionTwoId}
            sectionTwoListId={sectionTwoListId}
            signersList={signersList}
            resolveTable={resolveBulkSendTable}
            onSendBatch={(items) => handleSendToSignBatch(items)}
          />
          <JournalSectionPagination
            pagination={pagination}
            total={displayedRecords?.total || 0}
          />
        </div>
      </Flex>
        </>
      )}
    </>
  );
}
